import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { userAppContext } from '../../context/Context.tsx';
import {
  canExtendCommercialTrial,
  DEMO_BOOKED_TAG,
  hasBookedDemo,
  isEligibleForCommercialTrialExtension,
  isTrialExpired,
  getTrialDaysRemaining,
  mergeTrialAttributes,
} from '../../utils/trialUtils';
import TrialExpiredModal from '../User/TrialExpiredModal.tsx';
import ReviewingApplicationModal from '../User/ReviewingApplicationModal.tsx';
import AcademicApprovedTrialModal from '../User/AcademicApprovedTrialModal.tsx';
import AcademicApprovedTrialBanner from '../User/AcademicApprovedTrialBanner.tsx';
import BookDemoEmailModal from '../NavMenus/BookDemoEmailModal.tsx';
import { approveBookDemoEmail } from '../NavMenus/bookDemoSession';
import AcademicApplicationService, {
  inferAcademicActivationDeferred,
} from '../../services/AcademicApplicationService.ts';
import { refreshSession } from '../../services/RefreshSession';
import { useAuthStore } from '../../store/authStore';
import { enrichUserWithCognitoAttributes } from '../../utils/enrichUserWithCognitoAttributes';
import { logTrialDebug, logTrialEvaluation } from '../../utils/trialDebug';
import { NAVBAR_HEIGHT_PX } from '@/constants/layoutHeaderInset';

const GlobalTrialBanner = () => {
  const { groups: contextGroups, user: contextUser } = userAppContext();
  const authUser = useAuthStore(state => state.user);
  const authGroups = useAuthStore(state => state.groups);
  const groups = contextGroups?.length ? contextGroups : authGroups;
  const user = contextUser ?? authUser;
  const location = useLocation();
  const navigate = useNavigate();
  const userEmail = user?.email ?? null;
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('trial');
  const [modalDismissed, setModalDismissed] = useState(false);
  const [academicStatus, setAcademicStatus] = useState(null);
  const [academicActivationDeferred, setAcademicActivationDeferred] = useState(false);
  const [showAcademicApprovedModal, setShowAcademicApprovedModal] = useState(false);
  const [deferredBannerDismissed, setDeferredBannerDismissed] = useState(() => {
    const dismissTimestamp = localStorage.getItem('academic-approved-trial-dismissed');
    if (!dismissTimestamp) return false;
    const daysSinceDismiss = (Date.now() - parseInt(dismissTimestamp, 10)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismiss < 7) return true;
    localStorage.removeItem('academic-approved-trial-dismissed');
    return false;
  });
  const [trialType, setTrialType] = useState(null);
  const [trialAttributes, setTrialAttributes] = useState(null);
  const [activeCampaignTags, setActiveCampaignTags] = useState<string[]>([]);
  const [statusGroups, setStatusGroups] = useState<string[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusLoadedForEmail, setStatusLoadedForEmail] = useState<string | null>(null);
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false);
  const autoActivatingRef = useRef(false);
  const [siteHeaderTopPx, setSiteHeaderTopPx] = useState(NAVBAR_HEIGHT_PX);
  const isOnBookDemoPage = location.pathname === '/book-a-demo';
  const shouldSuppressTrialModal = isBookDemoModalOpen || isOnBookDemoPage;

  useEffect(() => {
    setShowModal(false);
    setModalType('trial');
    setModalDismissed(false);
    setAcademicStatus(null);
    setAcademicActivationDeferred(false);
    setShowAcademicApprovedModal(false);
    setTrialType(null);
    setTrialAttributes(null);
    setActiveCampaignTags([]);
    setStatusGroups([]);
    setLoadingStatus(false);
    setStatusLoadedForEmail(null);
    setIsBookDemoModalOpen(false);
  }, [userEmail]);

  useLayoutEffect(() => {
    const measureSiteHeader = () => {
      const header = document.querySelector('.site-header');
      setSiteHeaderTopPx(header ? Math.round(header.getBoundingClientRect().height) : NAVBAR_HEIGHT_PX);
    };

    measureSiteHeader();
    window.addEventListener('resize', measureSiteHeader);

    const header = document.querySelector('.site-header');
    const observer =
      header && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureSiteHeader) : null;
    if (header) {
      observer?.observe(header);
    }

    return () => {
      window.removeEventListener('resize', measureSiteHeader);
      observer?.disconnect();
    };
  }, [location.pathname]);

  const applyAcademicStatusResponse = (
    response: Awaited<ReturnType<typeof AcademicApplicationService.getAcademicStatus>>,
  ) => {
    setAcademicStatus(response.status);
    const deferred = inferAcademicActivationDeferred(response);
    setAcademicActivationDeferred(deferred);
    setTrialType(response.trialType);
    setStatusGroups(response.groups ?? []);
    useAuthStore.getState().setAcademicStatus(response.status);
    useAuthStore.getState().setAcademicActivationDeferred(deferred);
    setActiveCampaignTags((response.activeCampaignTags ?? []).map(tag => String(tag)));

    const nextTrialAttributes = {
      createdAt: response.createdAt ?? null,
      extendedTrialDays: response.extendedTrialDays != null ? response.extendedTrialDays : null,
      trialExtendedAt: response.trialExtendedAt ?? null,
    };
    setTrialAttributes(nextTrialAttributes);
    useAuthStore.getState().setTrialAttributes(nextTrialAttributes);
    useAuthStore.getState().setTrialAttributesLoaded(true);
    const currentUser = useAuthStore.getState().user;
    if (currentUser) {
      useAuthStore.getState().setUser(mergeTrialAttributes(currentUser, nextTrialAttributes));
    }
    if (response.trialType || response.userType) {
      useAuthStore.getState().setUserType(response.trialType || response.userType || null);
    }
  };

  useEffect(() => {
    if (!userEmail) {
      return;
    }

    let cancelled = false;

    const checkAcademicStatus = async () => {
      setLoadingStatus(true);
      try {
        const response = await AcademicApplicationService.getAcademicStatus(userEmail);
        if (cancelled) {
          return;
        }

        const contextRoles = groups || [];
        const actualRoles = response.groups || [];

        const roleChanged =
          (actualRoles.includes('ROLE_ACADEMIC') && !contextRoles.includes('ROLE_ACADEMIC')) ||
          (actualRoles.includes('ROLE_STANDARD') && !contextRoles.includes('ROLE_STANDARD')) ||
          (actualRoles.includes('ROLE_ADVANCED') && !contextRoles.includes('ROLE_ADVANCED')) ||
          (contextRoles.includes('ROLE_TRIAL') && !actualRoles.includes('ROLE_TRIAL'));

        if (roleChanged) {
          const reloadKey = `role-reload-${userEmail}`;
          const hasReloaded = sessionStorage.getItem(reloadKey);
          const reloadTimestamp = sessionStorage.getItem(`${reloadKey}-timestamp`);

          if (reloadTimestamp) {
            const timeSinceReload = Date.now() - parseInt(reloadTimestamp);
            if (timeSinceReload > 5 * 60 * 1000) {
              sessionStorage.removeItem(reloadKey);
              sessionStorage.removeItem(`${reloadKey}-timestamp`);
            }
          }

          if (!hasReloaded) {
            sessionStorage.setItem(reloadKey, 'true');
            sessionStorage.setItem(`${reloadKey}-timestamp`, Date.now().toString());

            try {
              await refreshSession({ forceRefresh: true });
              window.location.reload();
            } catch (error) {
              console.error('Error refreshing session:', error);
              window.location.reload();
            }
            return;
          } else {
            sessionStorage.removeItem(reloadKey);
            sessionStorage.removeItem(`${reloadKey}-timestamp`);
          }
        }

        applyAcademicStatusResponse(response);
        setStatusLoadedForEmail(userEmail);

        logTrialDebug('GlobalTrialBanner /academic-status response', {
          email: userEmail,
          status: response.status,
          academicActivationDeferred: response.academicActivationDeferred ?? null,
          trialType: response.trialType ?? null,
          createdAt: response.createdAt ?? null,
          extendedTrialDays: response.extendedTrialDays ?? null,
          trialExtendedAt: response.trialExtendedAt ?? null,
          hasBookedDemo: hasBookedDemo(response.activeCampaignTags),
          demoBookedTag: DEMO_BOOKED_TAG,
          activeCampaignTags: response.activeCampaignTags ?? [],
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error checking academic status:', error);
        setAcademicStatus(null);
        setTrialType(null);
        setActiveCampaignTags([]);
        setTrialAttributes(null);
        setStatusLoadedForEmail(userEmail);
      } finally {
        if (!cancelled) {
          setLoadingStatus(false);
        }
      }
    };

    checkAcademicStatus();

    return () => {
      cancelled = true;
    };
  }, [groups, userEmail]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !userEmail || academicStatus !== 'pending') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(userEmail);

        if (response.groups?.includes('ROLE_ACADEMIC')) {
          clearInterval(pollInterval);
          window.location.reload();
        }
      } catch (error) {
        console.error('Error polling academic status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, userEmail, academicStatus]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !userEmail || modalType !== 'book-demo-for-extension') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(userEmail);
        applyAcademicStatusResponse(response);

        if (hasBookedDemo(response.activeCampaignTags)) {
          clearInterval(pollInterval);
          setModalType('extension');
        }
      } catch (error) {
        console.error('Error polling demo booked status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, userEmail, modalType]);

  const activateDeferredAcademic = async () => {
    if (!userEmail) {
      throw new Error('User email not found');
    }

    const response = await AcademicApplicationService.activateAcademic(userEmail);
    if (response.status === 'error') {
      throw new Error(response.message);
    }

    localStorage.removeItem('academic-approved-trial-dismissed');
    setDeferredBannerDismissed(false);

    await refreshUserAttributes();
    await useAuthStore.getState().fetchUserType();
    sessionStorage.setItem('showAcademicUpgradeModal', '1');
    window.location.reload();
  };

  useEffect(() => {
    if (
      !groups?.includes('ROLE_TRIAL') ||
      !userEmail ||
      !user ||
      loadingStatus ||
      statusLoadedForEmail !== userEmail
    ) {
      return;
    }

    if (academicStatus !== 'approved' || !academicActivationDeferred) {
      return;
    }

    const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
    if (!isTrialExpired(trialUser)) {
      return;
    }

    if (autoActivatingRef.current) {
      return;
    }

    autoActivatingRef.current = true;
    activateDeferredAcademic().catch(error => {
      console.error('Error auto-activating deferred academic access:', error);
      autoActivatingRef.current = false;
    });
  }, [
    groups,
    userEmail,
    user,
    academicStatus,
    academicActivationDeferred,
    loadingStatus,
    statusLoadedForEmail,
    trialAttributes,
  ]);

  useEffect(() => {
    if (groups?.includes('ROLE_TRIAL') && userEmail) {
      if (loadingStatus || statusLoadedForEmail !== userEmail) {
        return;
      }
    }

    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;

    if (groups?.includes('ROLE_TRIAL') && user) {
      const dismissKey = 'reviewing-application-dismissed';
      const dismissTimestamp = localStorage.getItem(dismissKey);
      let reviewingModalDismissed = false;

      if (dismissTimestamp) {
        const daysSinceDismiss = (Date.now() - parseInt(dismissTimestamp)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) {
          reviewingModalDismissed = true;
        } else {
          localStorage.removeItem(dismissKey);
        }
      }

      const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
      const expired = isTrialExpired(trialUser);
      const eligibleForExtension = isEligibleForCommercialTrialExtension(trialUser, trialType);
      const canExtend = canExtendCommercialTrial(trialUser, trialType, activeCampaignTags);
      const deferredAcademicApproval = academicStatus === 'approved' && academicActivationDeferred;

      const academicApprovedDismissKey = 'academic-approved-trial-dismissed';
      const academicApprovedDismissTimestamp = localStorage.getItem(academicApprovedDismissKey);
      let academicApprovedModalDismissed = false;

      if (academicApprovedDismissTimestamp) {
        const daysSinceDismiss =
          (Date.now() - parseInt(academicApprovedDismissTimestamp)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) {
          academicApprovedModalDismissed = true;
        } else {
          localStorage.removeItem(academicApprovedDismissKey);
        }
      }

      logTrialEvaluation('GlobalTrialBanner modal decision', {
        user,
        trialAttributes,
        trialType,
        extra: {
          pathname: location.pathname,
          academicStatus,
          academicActivationDeferred,
          deferredAcademicApproval,
          modalDismissed,
          expired,
          eligibleForExtension,
          canExtend,
          hasBookedDemo: hasBookedDemo(activeCampaignTags),
          activeCampaignTags,
          modalTypeWillBe:
            academicStatus === 'pending' && !reviewingModalDismissed
              ? 'reviewing'
              : deferredAcademicApproval && !expired && !academicApprovedModalDismissed
                ? 'academic-approved'
                : expired && !modalDismissed && !deferredAcademicApproval
                  ? eligibleForExtension
                    ? canExtend
                      ? 'extension'
                      : 'book-demo-for-extension'
                    : 'trial'
                  : 'none',
        },
      });

      if (academicStatus === 'pending' && !reviewingModalDismissed) {
        setModalType('reviewing');
        setShowAcademicApprovedModal(false);
        showModalTimeout = setTimeout(() => setShowModal(true), 500);
      } else if (deferredAcademicApproval && !expired && !academicApprovedModalDismissed) {
        setShowModal(false);
        setShowAcademicApprovedModal(true);
      } else if (deferredAcademicApproval && !expired) {
        setShowModal(false);
        setShowAcademicApprovedModal(false);
      } else if (expired && !modalDismissed && !shouldSuppressTrialModal && !deferredAcademicApproval) {
        if (eligibleForExtension) {
          setModalType(canExtend ? 'extension' : 'book-demo-for-extension');
        } else {
          setModalType('trial');
        }
        setShowAcademicApprovedModal(false);
        showModalTimeout = setTimeout(() => setShowModal(true), 500);
      } else {
        setShowModal(false);
        if (!deferredAcademicApproval || expired || academicApprovedModalDismissed) {
          setShowAcademicApprovedModal(false);
        }
      }
    } else {
      setShowModal(false);
    }

    return () => {
      if (showModalTimeout) {
        clearTimeout(showModalTimeout);
      }
    };
  }, [
    groups,
    user,
    userEmail,
    modalDismissed,
    academicStatus,
    loadingStatus,
    statusLoadedForEmail,
    trialType,
    trialAttributes,
    activeCampaignTags,
    academicActivationDeferred,
    location.pathname,
    shouldSuppressTrialModal,
  ]);

  const refreshUserAttributes = async () => {
    const currentUser = await getCurrentUser();
    const session = await refreshSession({ forceRefresh: true });
    const userInfo = session.tokens.idToken.payload;

    const userWithAttributes = await enrichUserWithCognitoAttributes({
      ...userInfo,
      ...currentUser,
    });

    logTrialDebug('GlobalTrialBanner.refreshUserAttributes', {
      jwtAfterForceRefresh: userInfo,
      mergedUser: userWithAttributes,
    });

    useAuthStore.getState().setUser(userWithAttributes);
    return userWithAttributes;
  };

  const handleExtendTrial = async () => {
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const response = await AcademicApplicationService.extendCommercialTrial(user.email);

    if (response.status === 'error') {
      throw new Error(response.message);
    }

    await refreshUserAttributes();
    await useAuthStore.getState().fetchUserType();
    window.location.reload();
  };

  const handleOpenBookDemo = () => {
    setShowModal(false);
    setIsBookDemoModalOpen(true);
  };

  const handleProceedToBookDemo = (email: string) => {
    approveBookDemoEmail(email);
    setIsBookDemoModalOpen(false);
    setShowModal(false);
    navigate('/book-a-demo');
  };

  const handleCloseBookDemoModal = () => {
    setIsBookDemoModalOpen(false);

    if (groups?.includes('ROLE_TRIAL') && user && !modalDismissed && !isOnBookDemoPage) {
      const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
      if (isTrialExpired(trialUser)) {
        setShowModal(true);
      }
    }
  };

  const handleContinueAcademicTrial = () => {
    const dismissKey = 'academic-approved-trial-dismissed';
    localStorage.setItem(dismissKey, Date.now().toString());
    setDeferredBannerDismissed(true);
    setShowAcademicApprovedModal(false);
  };

  const handleCloseModal = () => {
    if (modalType === 'reviewing') {
      const dismissKey = 'reviewing-application-dismissed';
      localStorage.setItem(dismissKey, Date.now().toString());
    }
    setModalDismissed(true);
    setShowModal(false);
  };

  const trialUserForModal = user ? mergeTrialAttributes(user, trialAttributes ?? undefined) : null;
  const daysRemaining = trialUserForModal ? getTrialDaysRemaining(trialUserForModal) : 0;
  const trialExpired = trialUserForModal ? isTrialExpired(trialUserForModal) : false;
  const showDeferredAcademicBanner =
    Boolean((groups?.includes('ROLE_TRIAL') || statusGroups.includes('ROLE_TRIAL')) && user && userEmail) &&
    !loadingStatus &&
    statusLoadedForEmail === userEmail &&
    academicStatus === 'approved' &&
    academicActivationDeferred &&
    !trialExpired &&
    !deferredBannerDismissed;

  useEffect(() => {
    useAuthStore.getState().setShowDeferredAcademicTrialBanner(showDeferredAcademicBanner);
    return () => {
      useAuthStore.getState().setShowDeferredAcademicTrialBanner(false);
    };
  }, [showDeferredAcademicBanner]);

  return (
    <>
      {showDeferredAcademicBanner && (
        <AcademicApprovedTrialBanner
          topPx={siteHeaderTopPx}
          daysRemaining={daysRemaining}
          onActivate={activateDeferredAcademic}
          onContinueTrial={handleContinueAcademicTrial}
        />
      )}

      {showAcademicApprovedModal && (
        <AcademicApprovedTrialModal
          daysRemaining={daysRemaining}
          onActivate={activateDeferredAcademic}
          onContinueTrial={handleContinueAcademicTrial}
        />
      )}

      {showModal && modalType === 'reviewing' && <ReviewingApplicationModal onClose={handleCloseModal} />}

      {showModal && modalType !== 'reviewing' && (
        <TrialExpiredModal
          onClose={handleCloseModal}
          variant={
            modalType === 'extension'
              ? 'extension'
              : modalType === 'book-demo-for-extension'
                ? 'book-demo-for-extension'
                : 'expired'
          }
          onExtend={modalType === 'extension' ? handleExtendTrial : undefined}
          onBookDemo={modalType === 'book-demo-for-extension' ? handleOpenBookDemo : undefined}
        />
      )}

      <BookDemoEmailModal
        isOpen={isBookDemoModalOpen}
        onClose={handleCloseBookDemoModal}
        onProceed={handleProceedToBookDemo}
      />
    </>
  );
};

export default GlobalTrialBanner;
